import React, { useEffect, useRef } from "react";
import { Helmet } from 'react-helmet-async';
import { motion, useInView, useAnimation } from "framer-motion";
import MarketingLayout from "./MarketingLayout";
import { generateBreadcrumbJsonLd } from "./utils/breadcrumbJsonLd";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, Mail, MessageCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// アニメーション用のカスタムフック
const useScrollAnimation = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return { ref, controls, isInView };
};

type FaqItem = {
  q: string;
  a: string;
};

type FaqCategory = {
  category: string;
  color: string;
  questions: FaqItem[];
};

const faqs: FaqCategory[] = [
  {
    category: "サービスについて",
    color: "bg-yellow-400",
    questions: [
      {
        q: "JOYBASEとは何ですか？",
        a: "JOYBASEは、オリジナルパック（オリパ）サイトを簡単に開設・運営できるプラットフォームです。初期費用・月額費用0円で、トレーディングカードやグッズのオリパ販売を始められます。"
      },
      {
        q: "オリパとは何ですか？",
        a: "オリパ（オリジナルパック）は、トレーディングカードやグッズをランダムに封入したパックのことです。購入者は何が当たるかわからないワクワク感を楽しめます。"
      },
      {
        q: "複数のオリパサイトを運営できますか？",
        a: "現在は1アカウント1サイトですが、今後1つのアカウントで複数のサイトを運営できる機能を開発予定です。ブランドやカテゴリごとに分けて運営できるようになります。"
      }
    ]
  },
  {
    category: "料金について",
    color: "bg-green-400",
    questions: [
      {
        q: "初期費用や月額費用はかかりますか？",
        a: "初期費用・月額費用は一切かかりません。実際にユーザーがオリパを購入した際に、カード決済手数料3.6%とシステム利用料3%（合計6.6%）のみが発生します。"
      },
      {
        q: "手数料6.6%には何が含まれますか？",
        a: "カード決済手数料3.6%とシステム利用料3%が含まれています。ドメイン・サーバー費用もシステム利用料に含まれており、追加の費用は一切かかりません。※ご利用の決済手段によって手数料が異なる場合があります。"
      },
      {
        q: "最低利用期間はありますか？",
        a: "最低利用期間はありません。いつでも自由に始めて、いつでも停止できます。"
      }
    ]
  },
  {
    category: "始め方について",
    color: "bg-blue-400",
    questions: [
      {
        q: "利用開始に必要なものは何ですか？",
        a: "以下の2つが必要です：\n1. 古物商許可証（中古品を販売するために必要な許可証）\n2. 銀行口座（売上の入金先として）"
      },
      {
        q: "古物商許可証を持っていませんが、利用できますか？",
        a: "申し訳ございませんが、古物商許可証は法律で義務付けられているため、必須となります。最寄りの警察署で申請できます（申請から取得まで約40日）。"
      },
      {
        q: "どのくらいの時間で開設できますか？",
        a: "アカウント作成からサイト公開まで、最短5分程度で完了します。商品登録やデザイン調整に時間をかけることもできます。"
      },
      {
        q: "サイトのデザインはカスタマイズできますか？",
        a: "サイト名、ロゴ、サムネイル画像などの基本的なカスタマイズが可能です。イベント（オリパ）ごとに画像や説明文を自由に設定できます。"
      }
    ]
  },
  {
    category: "機能について",
    color: "bg-pink-400",
    questions: [
      {
        q: "どのような決済方法に対応していますか？",
        a: "現状クレジットカードのみ対応しています。今後、デビットカード、Apple Pay、Google Payなど決済方法を拡張していく予定です。"
      },
      {
        q: "fincodeとは何ですか？",
        a: "fincode（フィンコード）は、GMOイプシロン株式会社が提供するオンライン決済サービスです。JOYBASEでは、お客様からのカード決済を安全に受け付け、売上を店舗オーナーへ入金するための決済基盤として利用しています。ご利用開始にはfincode所定の加盟店審査が必要です。"
      },
      {
        q: "在庫管理はどのように行いますか？",
        a: "商品（カードやグッズ）を登録し、各イベント（オリパ）に配置します。抽選プールが自動生成され、在庫数に応じて販売が管理されます。"
      },
      {
        q: "配送管理はどのように行いますか？",
        a: "管理画面から配送状況を管理できます。追跡番号の登録や、ユーザーへの通知も可能です。"
      },
      {
        q: "ポイント機能とは何ですか？",
        a: "ユーザーがポイントを購入・チャージでき、そのポイントでオリパを引けるシステムです。リピート率向上に効果的です。"
      },
      {
        q: "SMS認証機能とは何ですか？",
        a: "ユーザーの本人確認のため、電話番号によるSMS認証を行う機能です。不正利用防止と信頼性向上に役立ちます。"
      }
    ]
  },
  {
    category: "技術的な問題",
    color: "bg-purple-400",
    questions: [
      {
        q: "ログインできません",
        a: "メールアドレスとパスワードが正しいかご確認ください。パスワードを忘れた場合は「パスワードをお忘れの方」からリセットできます。"
      },
      {
        q: "メールが届きません",
        a: "迷惑メールフォルダをご確認ください。また、メールアドレスが正しく登録されているか確認してください。"
      },
      {
        q: "画像がアップロードできません",
        a: "ファイルサイズが大きすぎる可能性があります。推奨サイズは5MB以下です。JPEG、PNG形式をご利用ください。"
      }
    ]
  },
  {
    category: "法的な問題",
    color: "bg-orange-400",
    questions: [
      {
        q: "古物商許可証はなぜ必要ですか？",
        a: "中古品（トレーディングカード含む）を販売する場合、古物営業法により古物商許可証が必要です。これは法律で義務付けられています。"
      },
      {
        q: "特定商取引法への対応は必要ですか？",
        a: "はい、販売者として特定商取引法に基づく表記が必要です。サイト設定から必要事項を登録できます。"
      },
      {
        q: "景品表示法への対応は必要ですか？",
        a: "はい、虚偽の表示や誇大広告は禁止されています。当選確率を正確に表示し、透明性のある運営を心がけてください。"
      }
    ]
  }
];

const FaqPage: React.FC = () => {
  // アニメーションバリアント
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const faqAnimation = useScrollAnimation();

  return (
    <MarketingLayout>
      <Helmet>
        <title>よくある質問（FAQ） | JOYBASE</title>
        <meta property="og:title" content="よくある質問（FAQ） | JOYBASE" />
        <meta name="description" content="JOYBASEに関するよくある質問と回答。サービス、料金、始め方、機能、技術的な問題などについて詳しく解説しています。" />
        <script type="application/ld+json">
        {generateBreadcrumbJsonLd([
          { name: "ホーム", url: "https://www.joybase.jp/" },
          { name: "よくある質問", url: "https://www.joybase.jp/faq" }
        ])}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#FFFEF5]">
        {/* ヘッダーセクション */}
        <section className="py-20 pt-32 relative overflow-hidden">
          {/* デコレーション */}
          <motion.div
            animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
            className="absolute top-32 left-[10%] w-12 h-12 bg-yellow-400 border-4 border-black rounded-lg shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hidden lg:block"
          />
          <motion.div
            animate={{ y: [0, 10, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 6, repeat: Infinity }}
            className="absolute top-40 right-[15%] w-10 h-10 bg-pink-400 border-4 border-black rounded-full shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hidden lg:block"
          />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-400 border-4 border-black rounded-2xl mb-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <HelpCircle className="w-10 h-10 text-black" />
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6">
                よくある<span className="relative inline-block">
                  <span className="relative z-10">質問</span>
                  <span className="absolute bottom-2 left-0 w-full h-4 bg-yellow-400 -z-0"></span>
                </span>
              </h1>
              <p className="text-xl text-gray-700 font-medium">
                JOYBASEに関するよくある質問と回答をまとめました
              </p>
            </motion.div>
          </div>
        </section>

        {/* FAQ セクション */}
        <motion.section
          ref={faqAnimation.ref}
          className="py-12"
          initial="hidden"
          animate={faqAnimation.controls}
          variants={containerVariants}
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {faqs.map((faqCategory, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                className="mb-8"
                variants={itemVariants}
              >
                <div className={`${faqCategory.color} border-4 border-black rounded-t-2xl px-6 py-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]`}>
                  <h2 className="text-xl font-black text-black">
                    {faqCategory.category}
                  </h2>
                </div>

                <div className="bg-white border-4 border-t-0 border-black rounded-b-2xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] overflow-hidden">
                  <Accordion type="single" collapsible className="w-full">
                    {faqCategory.questions.map((faq, questionIndex) => (
                      <AccordionItem
                        key={`${categoryIndex}-${questionIndex}`}
                        value={`item-${categoryIndex}-${questionIndex}`}
                        className="border-b-2 border-black last:border-b-0"
                      >
                        <AccordionTrigger className="px-6 py-4 text-left hover:bg-gray-50 text-base font-bold text-black">
                          {faq.q}
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-4 text-gray-700 whitespace-pre-line font-medium">
                          {faq.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* お問い合わせセクション */}
        <section className="py-16 bg-white border-t-4 border-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400 border-4 border-black rounded-2xl p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              whileHover={{ translateX: 2, translateY: 2, boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white border-4 border-black rounded-xl mb-4 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                  <MessageCircle className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-2xl font-black text-black mb-2">解決しない問題がありますか？</h3>
                <p className="text-black/80 font-medium mb-6">
                  お気軽にお問い合わせください。担当者が24時間以内にご返信いたします。
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white border-4 border-black rounded-xl font-bold text-lg shadow-[4px_4px_0px_0px_rgba(255,255,255,0.5)] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.5)] hover:translate-x-0.5 hover:translate-y-0.5 transition-all"
                >
                  <Mail className="w-5 h-5" />
                  お問い合わせ
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black relative overflow-hidden">
          {/* デコレーション */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-10 left-10 w-24 h-24 border-4 border-yellow-400/30 rounded-xl"
          />
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-10 right-10 w-32 h-32 border-4 border-pink-400/30 rounded-full"
          />

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6">
                <span className="text-yellow-400">オリパサイト</span>開設を申し込もう
              </h2>
              <p className="text-xl text-gray-300 mb-10 font-medium">
                初期費用・月額費用0円でサイト開設を申し込めます
              </p>
              <motion.a
                href="https://dashboard.joybase.jp/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-10 py-5 bg-yellow-400 text-black border-4 border-yellow-400 rounded-xl font-black text-lg shadow-[6px_6px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.3)] hover:translate-x-1 hover:translate-y-1 transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                無料でサイト開設を申し込む
                <ArrowRight className="w-5 h-5" />
              </motion.a>
            </motion.div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default FaqPage;
